<template>
    <div
        class="install-preview"
        :class="[
            `install-preview--${size}`,
            `install-preview--${resolvedPlatform}`
        ]"
    >
        <template v-if="resolvedPlatform === 'mobile'">
            <figure
                v-if="!failed.mobileHome"
                class="install-preview__figure install-preview__figure--home"
            >
                <button
                    type="button"
                    class="install-preview__open"
                    aria-label="Открыть изображение домашнего экрана в полном размере"
                    @click="openPreview('mobile-home')"
                >
                    <img
                        src="/install-previews/mobile-home-highlighted.png"
                        alt="Ярлык Аргуса на домашнем экране телефона"
                        loading="lazy"
                        decoding="async"
                        @error="markFailed('mobileHome')"
                    >
                    <span class="install-preview__zoom" aria-hidden="true">
                        <i class="fas fa-expand-alt"></i>
                        <span>Открыть</span>
                    </span>
                </button>
                <figcaption v-if="size === 'full'">Ярлык на экране телефона</figcaption>
            </figure>
            <figure
                v-if="size === 'full' && !failed.mobileApp"
                class="install-preview__figure install-preview__figure--app"
            >
                <button
                    type="button"
                    class="install-preview__open"
                    aria-label="Открыть изображение приложения в полном размере"
                    @click="openPreview('mobile-app')"
                >
                    <img
                        src="/install-previews/mobile-app.png"
                        alt="Аргус после запуска с домашнего экрана"
                        loading="lazy"
                        decoding="async"
                        @error="markFailed('mobileApp')"
                    >
                    <span class="install-preview__zoom" aria-hidden="true">
                        <i class="fas fa-expand-alt"></i>
                        <span>Открыть</span>
                    </span>
                </button>
                <figcaption>Аргус открывается как отдельное приложение</figcaption>
            </figure>
        </template>

        <figure
            v-else-if="!failed.desktop"
            class="install-preview__figure install-preview__figure--desktop"
        >
            <button
                type="button"
                class="install-preview__open"
                aria-label="Открыть изображение рабочего стола в полном размере"
                @click="openPreview('desktop')"
            >
                <img
                    src="/install-previews/desktop-highlighted.png"
                    alt="Ярлыки Аргуса на рабочем столе и панели задач"
                    loading="lazy"
                    decoding="async"
                    @error="markFailed('desktop')"
                >
                <span class="install-preview__zoom" aria-hidden="true">
                    <i class="fas fa-expand-alt"></i>
                    <span>Открыть</span>
                </span>
            </button>
            <figcaption v-if="size === 'full'">
                Ярлык на рабочем столе и Аргус на панели задач
            </figcaption>
        </figure>

        <div v-if="showFallback" class="install-preview__fallback">
            <img src="/icons/icon-192.png" alt="">
            <div>
                <strong>Аргус будет под рукой</strong>
                <span>
                    {{ resolvedPlatform === 'mobile'
                        ? 'На экране вашего телефона'
                        : 'На рабочем столе компьютера' }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import {isMobileInstallDevice} from '@/utils/appInstallation';

export default {
    name: 'InstallAppPreview',
    props: {
        size: {
            type: String,
            default: 'compact',
            validator: value => ['full', 'compact', 'mini'].includes(value)
        },
        platform: {
            type: String,
            default: 'auto',
            validator: value => ['auto', 'mobile', 'desktop'].includes(value)
        }
    },
    data() {
        return {
            failed: {
                mobileHome: false,
                mobileApp: false,
                desktop: false
            }
        };
    },
    computed: {
        resolvedPlatform() {
            if (this.platform !== 'auto') return this.platform;
            return isMobileInstallDevice() ? 'mobile' : 'desktop';
        },
        showFallback() {
            if (this.resolvedPlatform === 'desktop') return this.failed.desktop;
            if (this.size === 'full') {
                return this.failed.mobileHome && this.failed.mobileApp;
            }
            return this.failed.mobileHome;
        }
    },
    methods: {
        markFailed(key) {
            this.$set(this.failed, key, true);
        },
        previewFiles() {
            if (this.resolvedPlatform === 'desktop') {
                return [{
                    id: 'desktop',
                    name: 'Аргус на рабочем столе и панели задач.png',
                    url: '/install-previews/desktop-highlighted.png',
                    downloadUrl: '/install-previews/desktop-highlighted.png',
                    mimeType: 'image/png',
                    isImage: true
                }];
            }
            return [
                {
                    id: 'mobile-home',
                    name: 'Ярлык Аргуса на экране телефона.png',
                    url: '/install-previews/mobile-home-highlighted.png',
                    downloadUrl: '/install-previews/mobile-home-highlighted.png',
                    mimeType: 'image/png',
                    isImage: true
                },
                {
                    id: 'mobile-app',
                    name: 'Аргус после запуска.png',
                    url: '/install-previews/mobile-app.png',
                    downloadUrl: '/install-previews/mobile-app.png',
                    mimeType: 'image/png',
                    isImage: true
                }
            ];
        },
        openPreview(startId) {
            const files = this.previewFiles();
            if (this.$fileViewer) {
                this.$fileViewer.open(files, {
                    startId,
                    source: 'install-promotion'
                });
                return;
            }
            const file = files.find(item => item.id === startId) || files[0];
            window.open(file.url, '_blank', 'noopener,noreferrer');
        }
    }
};
</script>

<style scoped>
.install-preview {
    min-width: 0;
}

.install-preview--full {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
}

.install-preview--full.install-preview--desktop {
    grid-template-columns: 1fr;
}

.install-preview__figure {
    min-width: 0;
    margin: 0;
}

.install-preview__open {
    position: relative;
    display: block;
    width: 100%;
    padding: 0;
    overflow: hidden;
    border: 0;
    border-radius: 18px;
    background: transparent;
    cursor: zoom-in;
}

.install-preview__open:focus-visible {
    outline: 3px solid #5da4ff;
    outline-offset: 3px;
}

.install-preview__figure img {
    display: block;
    width: 100%;
    border: 1px solid rgba(34, 92, 165, .18);
    border-radius: 18px;
    background: #dfe7ef;
    box-shadow: 0 18px 44px rgba(24, 59, 108, .16);
}

.install-preview__zoom {
    position: absolute;
    right: 9px;
    bottom: 9px;
    display: inline-flex;
    min-height: 30px;
    align-items: center;
    gap: 6px;
    padding: 6px 9px;
    border: 1px solid rgba(255, 255, 255, .46);
    border-radius: 10px;
    color: #fff;
    background: rgba(9, 31, 61, .82);
    box-shadow: 0 5px 15px rgba(6, 20, 40, .28);
    font-size: 11px;
    line-height: 1;
    font-weight: 800;
    backdrop-filter: blur(5px);
}

.install-preview__open:hover .install-preview__zoom {
    background: rgba(8, 91, 196, .94);
}

.install-preview__figure figcaption {
    margin-top: 9px;
    color: #617187;
    font-size: 13px;
    line-height: 1.4;
    text-align: center;
}

.install-preview--mobile .install-preview__figure img {
    max-height: 620px;
    object-fit: contain;
}

.install-preview--compact .install-preview__figure,
.install-preview--mini .install-preview__figure {
    overflow: hidden;
    border-radius: 15px;
}

.install-preview--compact .install-preview__open,
.install-preview--mini .install-preview__open {
    border-radius: 15px;
}

.install-preview--compact .install-preview__figure img {
    height: 136px;
    border-radius: 15px;
    object-fit: cover;
}

.install-preview--compact.install-preview--mobile .install-preview__figure img {
    object-position: 76% 51%;
}

.install-preview--compact.install-preview--desktop .install-preview__figure img {
    object-position: 100% 100%;
}

.install-preview--mini .install-preview__figure img {
    height: 82px;
    border-radius: 13px;
    object-fit: cover;
}

.install-preview--mini .install-preview__zoom {
    right: 6px;
    bottom: 6px;
    min-height: 27px;
    padding: 5px 7px;
}

.install-preview--mini .install-preview__zoom span {
    display: none;
}

.install-preview--mini.install-preview--mobile .install-preview__figure img {
    object-position: 76% 51%;
}

.install-preview--mini.install-preview--desktop .install-preview__figure img {
    object-position: 100% 100%;
}

.install-preview__fallback {
    display: flex;
    min-height: 96px;
    align-items: center;
    gap: 12px;
    padding: 14px;
    border: 1px solid rgba(85, 145, 224, .25);
    border-radius: 15px;
    background: linear-gradient(135deg, #eef6ff, #fff);
    color: #153c72;
}

.install-preview__fallback img {
    width: 54px;
    height: 54px;
    flex: 0 0 54px;
    border-radius: 14px;
    box-shadow: 0 8px 20px rgba(28, 84, 156, .16);
}

.install-preview__fallback strong,
.install-preview__fallback span {
    display: block;
}

.install-preview__fallback strong {
    margin-bottom: 2px;
    font-size: 14px;
}

.install-preview__fallback span {
    color: #687b94;
    font-size: 12px;
}

@media (max-width: 767.98px) {
    .install-preview--full {
        grid-template-columns: 1fr;
    }

    .install-preview--full .install-preview__figure img {
        max-height: none;
    }
}
</style>
